<template>
  <div class="hm-r-rooms">
    <div
      v-for="(item, idx) in rooms"
      :key="item.id || idx"
      class="hm-r-rooms__item"
    >
      <div class="hm-r-rooms__status" :title="item.status.name">
        <component :is="statusIcon(item.status && item.status.code)" aria-hidden="true"/>
      </div>

      <div v-if="item.lock_assignment" class="hm-r-rooms__locker">
        <LockerIcon/>
      </div>

      <div class="hm-r-rooms__top">
        <div class="hm-r-rooms__left">
          <div class="hm-r-rooms__index">{{ idx + 1 }}.</div>

          <div class="hm-r-rooms__title">
            <span class="hm-r-rooms__roomno">
              <template v-if="item.room">{{ item.room.short_name }}</template>
              <img v-else src="@/assets/img/hm/warning.svg" alt="">
            </span>
            <span class="hm-r-rooms__name">{{ item.short_room.room_category.name }}</span>
          </div>

          <div class="hm-r-rooms__dates">
            <CalendarIcon aria-hidden="true"/>
            <span class="hm-r-rooms__date">{{ fmtDate(item.short_room.check_in) }}</span>
            <ArrowIcon class="hm-r-rooms__arrow" aria-hidden="true"/>
            <span class="hm-r-rooms__date">{{ fmtDate(item.short_room.check_out) }}</span>
          </div>
        </div>

        <div class="hm-r-rooms__right">
          <button type="button" class="hm-r-rooms__details-btn" @click="onDetailsClick(item.id)">Details</button>
          <HMKebabMenu :items="roomMenuItems" :context="item"/>
        </div>
      </div>

      <div class="hm-r-rooms__middle">
        <div class="hm-r-rooms__status-col">
          <span class="hm-r-rooms__balance-label">Balance:</span>
          <HMBalance :balance="item.balance"/>
        </div>
        <HMChipList>
          <HMChip>
            {{ item.short_room.nights || '0' }}
            <template #icon>
              <NightsIcon aria-hidden="true"/>
            </template>
          </HMChip>
          <HMChip
            v-if="item.reservation?.meal_plan?.name"
            :title="item.reservation.meal_plan.full_name"
            :color="item.reservation.meal_plan.color"
            isMeal
          >
            {{ item.reservation.meal_plan.name }}
          </HMChip>
          <HMChip v-if="item.short_room.accomodation_type?.age_assignment?.adult" color="#9AB992">
            {{ item.short_room.accomodation_type.age_assignment.adult }}
            <template #icon>
              <AdultIcon aria-hidden="true"/>
            </template>
          </HMChip>
          <HMChip v-if="item.short_room.accomodation_type?.age_assignment?.teenager" color="#76A9B4">
            {{ item.short_room.accomodation_type.age_assignment.teenager }}
            <template #icon>
              <TeenagerIcon aria-hidden="true"/>
            </template>
          </HMChip>
          <HMChip v-if="item.short_room.accomodation_type?.age_assignment?.child" color="#D493B0">
            {{ item.short_room.accomodation_type.age_assignment.child }}
            <template #icon>
              <ChildIcon aria-hidden="true"/>
            </template>
          </HMChip>
        </HMChipList>
      </div>

      <div class="hm-r-rooms__bottom">
        <app-select
          @close="onSelectBlur(item.id, item.guests)"
          v-model="item.guests"
          :options="guestsOptions"
          :searchable="false"
          :selectable="guest => canSelectGuest(item, guest)"
          placeholder="Choose guests"
          class="family"
          label="full_name"
          multiple
          :closeOnSelect="false"
          :readonly="isEditDisabled"
        />
      </div>
    </div>

    <div v-if="loading" class="hm-r-rooms__loading">Loading rooms…</div>
    <div v-else-if="!rooms.length" class="hm-r-rooms__empty">No rooms found.</div>

    <modal
      :adaptive="true"
      :clickToClose="false"
      :height="'auto'"
      :reset="true"
      :scrollable="true"
      :width="300"
      name="change-room"
      class="modal-not-overflow"
    >
      <div class="popup-new">
        <button class="popup-new__close" type="button" @click="$modal.hide('change-room')"></button>
        <div class="popup-new__title popup-new__title--mb-15">Change Room</div>
        <div class="popup-new__description">
          <template v-if="current_room && current_room.room && rooms_available.length > 0">
            {{ current_room.room.short_name || '' }} {{ current_room.short_room.room_category.name }}
          </template>
          <app-cells v-else :indent="false" gap="10px">
            <img src="@/assets/img/hm/warning.svg" alt=""> Unassigned
          </app-cells>
        </div>
        <div class="popup-new__body">
          <app-form-field label="Change to:" class="family">
            <app-select
              v-model="room_to_change"
              :reduce="item => item.id"
              :options="rooms_available"
              label="room_name"
              placeholder="Choose room"
              class="family"
            />
          </app-form-field>
          <app-cells :indent="false" margin-top="20px" position="end">
            <HMButton
              @click.native="onChangeRoom"
              :loading="isSubmittingChangeRoom"
              :disabled="!room_to_change"
            >
              Change Room
            </HMButton>
          </app-cells>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import LockerIcon from '@/views/hotel-management/reservations/detail/icons/LockerIcon.vue'
import CalendarIcon from '@/views/hotel-management/reservations/detail/icons/CalendarIcon.vue'
import ArrowIcon from '@/views/hotel-management/reservations/detail/icons/ArrowIcon.vue'
import NightsIcon from '@/views/hotel-management/reservations/detail/icons/NightsIcon.vue'
import AdultIcon from '@/views/hotel-management/reservations/detail/icons/AdultIcon.vue'
import TeenagerIcon from '@/views/hotel-management/reservations/detail/icons/TeenagerIcon.vue'
import ChildIcon from '@/views/hotel-management/reservations/detail/icons/ChildIcon.vue'
import CloseIcon from '@/views/hotel-management/reservations/detail/icons/CloseIcon.vue'
import HMBalance from '@/views/hotel-management/reservations/components/HMBalance.vue'
import HMChip from '@/views/hotel-management/reservations/components/HMChip.vue'
import HMChipList from '@/views/hotel-management/reservations/components/HMChipList.vue'
import CheckIn from '@/views/hotel-management/reservations/detail/icons/CheckIn.vue'
import CheckOut from '@/views/hotel-management/reservations/detail/icons/CheckOut.vue'
import NotCheckIn from '@/views/hotel-management/reservations/detail/icons/NotCheckIn.vue'
import HMKebabMenu from '@/components/HMKebabMenu.vue'
import { getHotelManagementRoomsAvailable, patchHotelManagementReservationsRooms } from '@/http'
import HMButton from '@/views/hotel-management/reservations/components/HMButton.vue'

export default {
  name: 'HMRoomsList',
  components: {
    HMButton,
    HMChipList,
    HMChip,
    HMBalance,
    CloseIcon,
    LockerIcon,
    CalendarIcon,
    ArrowIcon,
    NightsIcon,
    AdultIcon,
    TeenagerIcon,
    ChildIcon,
    CheckIn,
    CheckOut,
    NotCheckIn,
    HMKebabMenu,
  },
  props: {
    rooms: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    guestsOptions: { type: Array, default: () => [] },
    rData: { type: Object },
  },
  data() {
    return {
      current_room: {},
      rooms_available: [],
      room_to_change: null,
      isSubmittingChangeRoom: false,
    }
  },
  computed: {
    isEditDisabled() {
      return this.rData.reservation_hotel !== null
    },
    roomMenuItems() {
      return [
        {
          label: 'Lock Assignment',
          icon: 'lock-menu.svg',
          onClick: this.onLock,
          visible: (_menuItem, item) => !item.lock_assignment && !!item.room,
        },
        {
          label: 'Change Room',
          icon: 'change-menu.svg',
          onClick: this.onChange,
          visible: (_menuItem, item) => !item.lock_assignment,
        },
        {
          label: 'Unassign Room',
          icon: 'close.svg',
          onClick: this.onUnassign,
          visible: (_menuItem, item) => !item.lock_assignment && !!item.room && item.status.code !== 'checked_in',
        },
        {
          label: 'Unlock assignment',
          icon: 'unlock-menu.svg',
          onClick: this.onUnlock,
          visible: (_menuItem, item) => item.lock_assignment,
        },
      ]
    },
  },
  methods: {
    onChangeRoom() {
      this.isSubmittingChangeRoom = true
      patchHotelManagementReservationsRooms(this.current_room.id, { room: this.room_to_change })
        .finally(() => (this.isSubmittingChangeRoom = false))
        .then(() => {
          this.$modal.hide('change-room')
          this.$emit('update-rooms')
        })
    },
    onUnassign(room) {
      patchHotelManagementReservationsRooms(room.id, { room: null })
        .then(() => { this.$emit('update-rooms') })
    },
    onUnlock(room) {
      patchHotelManagementReservationsRooms(room.id, { lock_assignment: false })
        .then(() => { this.$emit('update-rooms') })
    },
    onLock(room) {
      patchHotelManagementReservationsRooms(room.id, { lock_assignment: true })
        .then(() => { this.$emit('update-rooms') })
    },
    onChange(room) {
      this.current_room = { ...room }
      getHotelManagementRoomsAvailable({
        from_date: room.short_room.check_in,
        to_date: room.short_room.check_out,
        room_category: room.short_room.room_category.id,
        property: room.reservation.property.code
      })
        .then(res => {
          this.rooms_available = res.data
          this.$modal.show('change-room')
        })
    },
    onDetailsClick(id) {
      this.$router.push({
        name: 'hotel-management-reservations-detail-rooms-detail',
        params: { id: this.$route.params.id, roomId: id }
      })
    },
    onSelectBlur(id, guests) {
      patchHotelManagementReservationsRooms(id, { guests: guests.map(item => item.id) })
    },
    canSelectGuest(item, guest) {
      const pax = item.short_room?.accomodation_type?.pax ?? Infinity
      const selected = Array.isArray(item.guests) ? item.guests : []
      const alreadySelected = selected.some(g => g.id === guest?.id)

      return selected.length < pax || alreadySelected
    },
    statusIcon(code) {
      const map = { checked_in: 'CheckIn', checked_out: 'CheckOut', not_checked_in: 'NotCheckIn' }
      return map[code] || 'NotCheckIn'
    },
    fmtDate(iso) {
      if (!iso) return ''
      const d = new Date(`${ iso }T00:00:00`)
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${ d.getDate() } ${ months[d.getMonth()] }, ${ d.getFullYear() }`
    }
  }
}
</script>

<style>
.hm-r-rooms {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.hm-r-rooms__item {
  position: relative;
  border-radius: 16px;
  border: 1px solid #e9e9e9;
  background-color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, .06);
  padding: 14px;
}

.hm-r-rooms__status {
  position: absolute;
  top: 48px;
  left: -9px;
  width: 38px;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-image: url('~@/views/hotel-management/reservations/detail/icons/plug-status.svg');
}

.hm-r-rooms__locker {
  position: absolute;
  top: -4px;
  left: 45px;
}

.hm-r-rooms__top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.hm-r-rooms__left {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  margin-left: 4px;
}

.hm-r-rooms__index {
  color: #000;
  font-family: 'MazzardSoftH', sans-serif;
  font-size: 16px;
  font-weight: 600;
}

.hm-r-rooms__title {
  display: inline-flex;
  align-items: center;
  gap: 12px;
  width: 212px;
}

.hm-r-rooms__roomno {
  color: #000;
  font-family: 'MazzardSoftH', sans-serif;
  font-size: 16px;
  font-weight: 600;
  line-height: 1;
}

.hm-r-rooms__name {
  color: #000;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 15px;
  font-weight: 500;
}

.hm-r-rooms__dates {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #000;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 15px;
  line-height: 1;
  font-weight: 500;
}

.hm-r-rooms__right {
  display: inline-flex;
  align-items: center;
  gap: 20px;
}

.hm-r-rooms__details-btn {
  height: 36px;
  padding: 0 13px;
  border: 0;
  border-radius: 8px;
  background: linear-gradient(180deg, #E3E8D9 0%, #DCE4CD 100%);
  color: #698262;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 14px;
  line-height: 1;
  font-weight: 500;
  cursor: pointer;
  transition: filter 0.3s;
}

.hm-r-rooms__details-btn:hover {
  filter: brightness(0.95);
}

.hm-r-rooms__middle {
  display: grid;
  grid-template-columns: 230px 1fr;
  align-items: center;
  margin-top: 10px;
  padding-left: 30px;
}

.hm-r-rooms__status-col {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.hm-r-rooms__balance-label {
  color: #666;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 12px;
  font-weight: 500;
  letter-spacing: .5px;
  text-transform: uppercase;
}

.hm-r-rooms__loading, .hm-r-rooms__empty {
  color: #8A8A8A;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 14px;
  text-align: center;
  padding: 8px 0;
}

.hm-r-rooms__bottom {
  margin-top: 10px;
}
</style>
